import { uploadPicture } from '@Services/uploads/uploads.req';
import {
	ALLOWED_IMAGE_TYPES,
	MAX_IMAGE_BYTES,
} from '@RichTextEditor/imageUtils';
import type { QuestionCsv } from './questionCsv';

/**
 * Local images for the questions import. The sheet's `image` cell holds a
 * link (the server fetches it) or a path/file name from the teacher's PC
 * (`C:\exam\q5.png`, `q5.png`) — the browser cannot read a path, so the
 * teacher picks the files and they are matched by file name, ignoring the
 * folder and letter case, uploaded, and the cell is rewritten to the URL.
 */

export type ImageCell = 'none' | 'link' | 'local';

export const classifyImageCell = (value?: string): ImageCell => {
	const cell = (value ?? '').trim();
	if (cell === '') return 'none';
	return /^https?:\/\//i.test(cell) ? 'link' : 'local';
};

export const basenameOf = (value: string) =>
	(value.trim().split(/[\\/]/).pop() || '').toLowerCase();

export type ImageCheck = {
	/** Rows per question type, as written in the sheet. */
	types: Record<string, number>;
	links: number;
	/** File names the sheet references locally. */
	referenced: string[];
	matched: Map<string, File>;
	missing: string[];
	/** Two picked files share one name — which one is meant is unknowable. */
	duplicates: string[];
	invalid: { name: string; reason: string }[];
};

export const checkQuestionImages = (
	csv: QuestionCsv,
	picked: File[]
): ImageCheck => {
	const types: Record<string, number> = {};
	let links = 0;
	const referenced = new Set<string>();
	csv.rows.forEach((row) => {
		const type = (row.type ?? '').trim().toLowerCase() || '(empty)';
		types[type] = (types[type] ?? 0) + 1;
		const kind = classifyImageCell(row.image);
		if (kind === 'link') links += 1;
		if (kind === 'local') referenced.add(basenameOf(row.image));
	});

	const byName = new Map<string, File[]>();
	picked.forEach((file) => {
		const name = file.name.toLowerCase();
		byName.set(name, [...(byName.get(name) ?? []), file]);
	});

	const matched = new Map<string, File>();
	const missing: string[] = [];
	const duplicates: string[] = [];
	const invalid: { name: string; reason: string }[] = [];
	referenced.forEach((name) => {
		const files = byName.get(name);
		if (!files) return missing.push(name);
		if (files.length > 1) return duplicates.push(name);
		const [file] = files;
		if (!ALLOWED_IMAGE_TYPES.includes(file.type))
			invalid.push({ name, reason: 'not a PNG, JPG, GIF or WebP image' });
		else if (file.size > MAX_IMAGE_BYTES)
			invalid.push({ name, reason: 'larger than 5 MB' });
		else matched.set(name, file);
	});
	return {
		types,
		links,
		referenced: Array.from(referenced),
		matched,
		missing,
		duplicates,
		invalid,
	};
};

/**
 * Longest edge capped, re-encoded to WebP. GIFs pass through untouched (a
 * canvas keeps only the first frame), and so does any file the encode
 * would not make smaller.
 */
export const downscaleToWebp = async (
	file: File,
	maxEdge = 1600,
	quality = 0.8
): Promise<File> => {
	if (file.type === 'image/gif') return file;
	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
		const canvas = document.createElement('canvas');
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);
		canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close();
		const blob = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, 'image/webp', quality)
		);
		if (!blob || blob.type !== 'image/webp' || blob.size >= file.size) return file;
		// The upload validator checks the extension, so the name must say .webp.
		const base = file.name.replace(/\.[^.]+$/, '') || 'image';
		return new File([blob], `${base}.webp`, { type: 'image/webp' });
	} catch {
		return file;
	}
};

/**
 * Uploads every matched file (a few at a time) and returns
 * file name → stored URL. The first failure rejects the whole batch: the
 * import has not been sent yet, so nothing half-applies.
 */
export const uploadQuestionImages = async (
	matched: Map<string, File>,
	onProgress: (done: number, total: number) => void,
	concurrency = 3
): Promise<Map<string, string>> => {
	const entries = Array.from(matched.entries());
	const urls = new Map<string, string>();
	let next = 0;
	let done = 0;
	onProgress(0, entries.length);
	const worker = async () => {
		while (next < entries.length) {
			const [name, file] = entries[next];
			next += 1;
			const photo = await downscaleToWebp(file);
			const { data } = await uploadPicture({ folder: 'question-images', photo });
			urls.set(name, data.data.uri);
			done += 1;
			onProgress(done, entries.length);
		}
	};
	await Promise.all(
		Array.from({ length: Math.min(concurrency, entries.length) }, worker)
	);
	return urls;
};

/** Local cells → uploaded URL; links and unmatched names stay as written (the server fails those rows). */
export const rewriteImageCells = (
	csv: QuestionCsv,
	urls: Map<string, string>
): QuestionCsv => ({
	fields: csv.fields,
	rows: csv.rows.map((row) => {
		if (classifyImageCell(row.image) !== 'local') return row;
		const url = urls.get(basenameOf(row.image));
		return url ? { ...row, image: url } : row;
	}),
});
